import { CustomButton } from "@/components/custom/custom-button";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DataTable } from "@/components/custom/data-table";
import { Box, Stack, styled } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomPagination from "@/components/custom/custom-pagination";
import { GL_STATUS } from "../data";
import { useExportCsv } from "@/hooks/use-export-csv";
import { getGlAccountColumns } from "./_components/gl-account-column";
import GlDialog from "./_components/gl-dialog";
import { useGlAccounts } from "@/hooks/use-gl-accounts";
import { createGlAccount, updateGlAccount } from "@/services/gl.service";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const GlAccounts = () => {
  const [glDialogOpen, setGlDialogOpen] = useState(false);
  const [selectedGl, setSelectedGl] = useState(null);

  const [searchParams] = useSearchParams();

  const searchValue = searchParams.get("search") || "";
  const statusFilter = searchParams.get("isActive") || "";
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);

  const queryParams = {
    page: currentPage,
    size: pageSize,
    search: searchValue,
    isActive: statusFilter || undefined,
  };

  const {
    data: glAccounts,
    isLoading,
    isError,
  } = useGlAccounts(true, queryParams);

  const handleEditClick = (gl) => {
    setSelectedGl(gl);
    setGlDialogOpen(true);
  };

  const handleDialogClose = () => {
    setGlDialogOpen(false);
    setSelectedGl(null);
  };

  const mutationFn = (payload) =>
    selectedGl
      ? updateGlAccount({ id: selectedGl.id, ...payload })
      : createGlAccount(payload);

  // const { exportToCsv, isExporting } = useExportCsv({
  //   fetchFn,
  //   params: queryParams,
  //   filename: "gl-accounts",
  //   transform: (res) =>
  //     res?.data?.map(
  //       ({ code, name, category, accountType, normalBalance, isActive }) => ({
  //         "Account Code": code,
  //         "Account Name": name,
  //         Category: category,
  //         "Account Type": accountType,
  //         "Normal Balance": normalBalance,
  //         Status: isActive ? "Active" : "Inactive",
  //       }),
  //     ) ?? [],
  // });

  const isExporting = false;

  return (
    <RootWrapper>
      <Card>
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={3}>
            <CustomSearchInput
              paramKey="search"
              placeholder="Search by account code or name..."
            />
            <CustomSelectInput
              paramKey="isActive"
              label="Status"
              placeholder="All Statuses"
              options={GL_STATUS}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CustomButton
              variantType="outline"
              endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              // onClick={exportToCsv}
              // isLoading={isExporting}
              sx={{ height: 35 }}
            >
              {isExporting ? "Exporting..." : "Export"}
            </CustomButton>
            <CustomButton
              onClick={() => setGlDialogOpen(true)}
              sx={{ height: 35 }}
            >
              New GL Account
            </CustomButton>
          </Stack>
        </Stack>

        <DataTable
          data={glAccounts?.data?.data ?? []}
          columns={getGlAccountColumns(handleEditClick)}
          isLoading={isLoading}
          isError={isError}
        />

        <CustomPagination
          total={glAccounts?.data?.totalCount}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>

      <GlDialog
        open={glDialogOpen}
        onClose={handleDialogClose}
        gl={selectedGl}
        mutationFn={mutationFn}
      />
    </RootWrapper>
  );
};

export default GlAccounts;
